import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import "../styles/admin.css";

function isStrongPassword(value) {
  return (
    String(value || "").length >= 8 &&
    /[A-Z]/.test(value) &&
    /[a-z]/.test(value) &&
    /\d/.test(value)
  );
}

export default function ChangePassword() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [visibleFields, setVisibleFields] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setMsg("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMsg("");

    if (!isStrongPassword(form.newPassword)) {
      setMsg("New password must include uppercase, lowercase, number, and at least 8 characters.");
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setMsg("New password and confirmation do not match.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.post("/auth/change-password", {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });

      const updatedUser = {
        ...(user || {}),
        ...(res.data.user || {}),
        mustChangePassword: false,
      };
      login({ token: localStorage.getItem("token"), user: updatedUser });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setMsg(err?.response?.data?.message || "Failed to update password.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVisibility = (key) => {
    setVisibleFields((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <main className="admin-login-page">
      <section className="admin-login-shell admin-login-shell--compact">
        <div className="admin-login-formPane">
          <div className="admin-login-formWrap">
            <p className="admin-login-form__eyebrow">Account Security</p>
            <h2>Change Password</h2>
            <p className="admin-login-form__subtitle">
              Update your temporary password before opening the dashboard.
            </p>

            <form onSubmit={handleSubmit} className="admin-login-form">
              <label className="admin-login-label">Temporary Password</label>
              <div className="admin-login-inputWrap">
                <span className="admin-login-inputIcon">#</span>
                <input
                  className="admin-login-input admin-login-input--password"
                  type={visibleFields.currentPassword ? "text" : "password"}
                  value={form.currentPassword}
                  onChange={(event) =>
                    updateField("currentPassword", event.target.value)
                  }
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="admin-login-toggle"
                  onClick={() => toggleVisibility("currentPassword")}
                  aria-label={
                    visibleFields.currentPassword
                      ? "Hide temporary password"
                      : "Show temporary password"
                  }
                >
                  {visibleFields.currentPassword ? "Hide" : "Show"}
                </button>
              </div>

              <label className="admin-login-label">New Password</label>
              <div className="admin-login-inputWrap">
                <span className="admin-login-inputIcon">#</span>
                <input
                  className="admin-login-input admin-login-input--password"
                  type={visibleFields.newPassword ? "text" : "password"}
                  value={form.newPassword}
                  onChange={(event) =>
                    updateField("newPassword", event.target.value)
                  }
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="admin-login-toggle"
                  onClick={() => toggleVisibility("newPassword")}
                  aria-label={
                    visibleFields.newPassword
                      ? "Hide new password"
                      : "Show new password"
                  }
                >
                  {visibleFields.newPassword ? "Hide" : "Show"}
                </button>
              </div>

              <label className="admin-login-label">Confirm New Password</label>
              <div className="admin-login-inputWrap">
                <span className="admin-login-inputIcon">#</span>
                <input
                  className="admin-login-input admin-login-input--password"
                  type={visibleFields.confirmPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={(event) =>
                    updateField("confirmPassword", event.target.value)
                  }
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="admin-login-toggle"
                  onClick={() => toggleVisibility("confirmPassword")}
                  aria-label={
                    visibleFields.confirmPassword
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                >
                  {visibleFields.confirmPassword ? "Hide" : "Show"}
                </button>
              </div>

              <button
                type="submit"
                className="admin-login-button"
                disabled={submitting}
              >
                {submitting ? "Updating..." : "Update Password"}
              </button>
            </form>

            {msg ? <p className="admin-login-error">{msg}</p> : null}
          </div>
        </div>
      </section>
    </main>
  );
}
